/**
 * Builder view-model — verbatim port of renderVals' builder section:
 * fieldRows (confidence colors, status, candidates, search), direction issues,
 * version VM, builder status, approve summary.
 */
import { useMemo } from 'react';
import { useBridge } from '../../store/bridgeStore';
import { allInts } from '../../domain/catalog';
import { versionsFor, selectedVersionId } from '../../domain/versions';
import { nodeDefs, fieldId, unresolvedCount, type Resolutions } from '../../domain/mapping';
import { fieldsFromEntityDraft, candidatesFor } from '../../domain/entityMapping';
import { DISC, VENDOR_SCHEMA, CANON_SCHEMA } from '../../data/seeds';
import type { MappingField } from '../../domain/types';

const classLabelMap: Record<string, string> = { mcp: 'MCP', openapi: 'OpenAPI', custom: 'custom' };
const verStatusMap: Record<string, { label: string; tone: string }> = {
  draft: { label: 'Draft · not published', tone: 'warn' },
  published: { label: 'Published · live', tone: 'success' },
  deprecated: { label: 'Deprecated · read-only', tone: 'neutral' },
};
const statusWord: Record<string, string> = { draft: 'draft', published: 'live', deprecated: 'deprecated' };

export function useBuilder() {
  const { state } = useBridge();

  return useMemo(() => {
    const s = state;
    const ints = allInts(s.adapters, s.metaOverrides);
    // activeAdapter
    const fromState = s.adapters.find(a => a.id === s.activeAdapterId);
    const intMatch = ints.find(i => i.id === s.activeAdapterId);
    const adapter =
      fromState ||
      (intMatch
        ? { id: intMatch.id, name: intMatch.name, initials: intMatch.initials, color: intMatch.color, cls: intMatch.cls, source: 'mapping · catalog v1', version: (intMatch.versions && intMatch.versions[0]) || '1.0', company: intMatch.company, category: intMatch.category, approved: true }
        : s.adapters[0]);

    const vendorName = adapter.name;
    const dir = s.direction;
    const R = s.resolutions;

    const vs = versionsFor(adapter.id, s.versions, s.adapters);
    const selId = selectedVersionId(adapter.id, s.versions, s.adapters, s.versionSel);
    // Defensive (FE↔BE integration): a connector could momentarily resolve to an
    // empty version list (e.g. backend version state in flux). Fall back to a
    // synthetic draft placeholder rather than crashing on `selVer.status`.
    const selVer =
      vs.find(v => v.id === selId) ||
      vs[0] || { id: adapter.id + '|draft', catalog: 'v1', vendor: adapter.version || '1.0', status: 'draft' as const, tag: null, rev: 0, execs: 0 };
    const isDraft = selVer.status === 'draft';

    // Reff: drafts use live resolutions; published/deprecated show resolved defaults.
    const Reff: Resolutions = isDraft
      ? R
      : { subject: R.subject || 'consult.animal_id', name: R.name || 'Contact.display_name' };

    // Real backend data (entity-mapping/auto), when loaded for the active
    // connector, takes over field rows AND candidates entirely — the mock
    // nodeDefs/DISC/VENDOR_SCHEMA scenario only renders when there's no real
    // connection yet, so real and mock data are never silently blended.
    const em = s.entityMapping;
    const fields: MappingField[] = em ? fieldsFromEntityDraft(em, dir) : nodeDefs(dir, vendorName, Reff).fields;
    const targetCatalog = em
      ? em.vendorSchemaFields.map(f => ({ label: f, type: em.vendorFieldTypes[f] ?? '' }))
      : dir === 'outbound' ? VENDOR_SCHEMA : CANON_SCHEMA;
    const sq = (s.fieldSearch || '').trim().toLowerCase();

    const fieldRows = fields.map((f: MappingField) => {
      const fid = fieldId(dir, f);
      const override = s.overrides[fid];
      const discResolved = !em && f.disc && Reff[f.disc as 'subject' | 'name'];
      const effectiveTo = override || (discResolved ? Reff[f.disc as 'subject' | 'name'] : f.to);
      const userTouched = !!override || !!discResolved;
      const isNeeds = em ? f.state === 'needs' && !override : isDraft && !!f.disc && !Reff[f.disc as 'subject' | 'name'] && !override;
      const expanded = s.expandedField === fid;
      const confPct = userTouched ? 100 : parseInt(f.conf, 10) || 0;
      const confLabel = userTouched ? '100%' : f.conf;
      const confColor = userTouched ? 'var(--accent)' : confPct >= 90 ? 'var(--success)' : confPct >= 75 ? 'var(--info)' : 'var(--warn)';
      const canon = dir === 'outbound' ? { name: f.from, type: f.fromType } : { name: effectiveTo, type: f.toType };
      const vend = dir === 'outbound' ? { name: effectiveTo, type: f.toType } : { name: f.from, type: f.fromType };
      const d = !em && f.disc ? DISC[f.disc] : null;

      let candidates;
      if (em && f.disc) {
        // Real, ranked candidates from the AI's actual proposal for this field.
        candidates = candidatesFor(em, f.disc).map(c => ({ ...c, isCurrent: c.label === effectiveTo }));
      } else if (d) {
        candidates = d.candidates.map(c => ({ label: c.label, conf: c.conf, note: c.note, isCurrent: c.label === effectiveTo }));
      } else {
        const alts = targetCatalog.filter(t => t.label !== f.to).slice(0, 2);
        candidates = [{ label: f.to, conf: f.conf, note: 'current AI mapping', isCurrent: !override }]
          .concat(alts.map(t => ({ label: t.label, conf: '·', note: t.type, isCurrent: false })))
          .map(c => ({ ...c, isCurrent: c.label === effectiveTo }));
      }

      const searchResults =
        expanded && sq
          ? targetCatalog
              .filter(t => t.label.toLowerCase().includes(sq) || t.type.toLowerCase().includes(sq))
              .map(t => ({ label: t.label, type: t.type, isCurrent: t.label === effectiveTo }))
          : [];

      let statusLabel: string;
      let statusTone: string;
      let statusIcon: 'warn' | 'check' | 'ai';
      if (isNeeds) { statusLabel = 'Needs review'; statusTone = 'warn'; statusIcon = 'warn'; }
      else if (userTouched) { statusLabel = f.disc ? 'Resolved · you' : 'Edited · you'; statusTone = 'accent'; statusIcon = 'check'; }
      else { statusLabel = 'AI mapped'; statusTone = 'info'; statusIcon = 'ai'; }

      return {
        ...f, fid,
        canonName: canon.name, canonType: canon.type,
        vendName: vend.name, vendType: vend.type,
        confWidth: confPct + '%', confColor, conf: confLabel,
        isNeeds, expanded, canRework: !isNeeds,
        statusLabel, statusTone, statusIcon,
        discTitle: d ? d.title : null,
        discDesc: d ? d.desc : 'AI matched this with ' + f.conf + ' confidence. Review it, pick another suggestion, or search the schema to remap.',
        candidates, candidatesHeading: d ? 'Pick the mapping' : 'Suggestions',
        searchResults, hasSearch: searchResults.length > 0, searchEmpty: expanded && !!sq && searchResults.length === 0,
        canReset: !!override || !!discResolved,
        remember: !em && f.disc ? s.remember[f.disc as 'subject' | 'name'] : false,
        // "Remember for <vendor>" only applies to the mock's genuinely
        // ambiguous fields (subject/name) — real data marks every field with
        // `disc` (to key its candidate list), so this must not key off that.
        showRemember: !em && !!f.disc,
        arrowRotate: dir === 'outbound' ? '0deg' : '180deg',
        arrowColor: isNeeds ? 'var(--warn)' : userTouched ? 'var(--accent)' : 'var(--line-strong)',
        rowBg: isNeeds ? 'var(--warn-soft)' : 'var(--card)',
        rowBorder: isNeeds ? 'var(--warn-line)' : expanded ? 'var(--accent-line)' : 'var(--line)',
        rowStripe: isNeeds ? 'var(--warn)' : userTouched ? 'var(--accent)' : 'var(--line-strong)',
        panelBorder: isNeeds ? 'var(--warn-line)' : 'var(--line)',
        panelBg: isNeeds ? 'var(--warn-soft)' : 'var(--paper-2)',
      };
    });

    const needsCount = fieldRows.filter(f => f.isNeeds).length;
    const settledCount = fieldRows.length - needsCount;
    const aiCount = fieldRows.filter(f => f.statusIcon === 'ai').length;
    const meanConf = Math.round(fieldRows.reduce((a, f) => a + (parseInt(f.conf, 10) || 0), 0) / fieldRows.length) + '%';

    // Real data: "unresolved" = fields the AI found zero candidates for
    // (already counted as needsCount above). Mock: the fixed subject/name
    // discrepancy concept.
    const unresolved = em ? needsCount : unresolvedCount(R);
    let builderStatus: string;
    let builderTone: string;
    let showApprove = false;
    if (selVer.status === 'published') { builderStatus = 'Published · live'; builderTone = 'success'; }
    else if (selVer.status === 'deprecated') { builderStatus = 'Deprecated · read-only'; builderTone = 'neutral'; }
    else if (unresolved > 0) { builderStatus = 'Needs resolution · ' + unresolved; builderTone = 'warn'; showApprove = true; }
    else { builderStatus = 'Ready to approve'; builderTone = 'accent'; showApprove = true; }

    const verView = verStatusMap[selVer.status] || verStatusMap.draft;
    const versionOptions = vs.map(v => ({
      value: v.id,
      label: v.catalog + ' × ' + v.vendor + ' · rev ' + (v.rev || 0) + ' · ' + statusWord[v.status] + (v.tag ? ' (' + v.tag + ')' : ''),
    }));

    const builderAdapterOptions = ints.filter(i => i.cls !== 'mcp').map(i => ({ value: i.id, label: i.name }));

    return {
      adapter,
      vendorName,
      classLabel: classLabelMap[adapter.cls] || adapter.cls,
      sourceLabel: (adapter as any).source,
      dir,
      fieldRows,
      needsCount, settledCount, aiCount, meanConf,
      hasIssues: needsCount > 0,
      issueCountLabel: needsCount + (needsCount === 1 ? ' field' : ' fields'),
      aiNoteLabel: aiCount + (aiCount === 1 ? ' field was' : ' fields were') + ' auto-mapped by AI — review and rework any before approving.',
      settledLabel: settledCount + '/' + fieldRows.length,
      // Real data only reflects the CURRENTLY selected direction (em is
      // direction-specific); the other direction's badge count is unknown
      // without a second fetch, so it reads 0 rather than showing a stale
      // mock number. Mock scenario keeps its original subject/name badges.
      outIssues: em ? (dir === 'outbound' ? needsCount : 0) : Reff.subject ? 0 : 1,
      inIssues: em ? (dir === 'inbound' ? needsCount : 0) : Reff.name ? 0 : 1,
      directionHint: dir === 'outbound' ? 'Writes from SK products into ' + vendorName : 'Reads from ' + vendorName + ' into SK products',
      schemaLabel: dir === 'outbound' ? vendorName + ' fields' : 'canonical fields',
      builderStatus, builderTone, showApprove,
      selVer,
      verStatusLabel: verView.label, verTone: verView.tone,
      verExecs: (selVer.execs || 0).toLocaleString() + ' runs',
      verRev: selVer.rev || 0,
      versionOptions, selVersionId: selId,
      builderAdapterOptions,
      approveSummary: em
        ? { confidence: meanConf, settled: settledCount, total: fieldRows.length, resolved: fieldRows.length - unresolved }
        : { confidence: '92%', settled: dir === 'outbound' ? 9 : 6, total: dir === 'outbound' ? 11 : 8, resolved: 2 - unresolved },
      unresolved,
      entityMappingLoading: s.entityMappingLoading,
      entityMappingError: s.entityMappingError,
      hasRealMapping: !!em,
    };
  }, [state]);
}
